import rawCatalog from '../assets/pixel_citizen_catalog.json';

/**
 * Avatars Pixel Citizen (style TDSM de Jordan Bunke) — format d'apparence,
 * catalogue des choix proposés et validation. Partagé client/serveur, sans
 * Phaser (sous-chemin `@campus/core/pixel-citizen`, cf. piège du barrel).
 *
 * Le catalogue (`assets/pixel_citizen_catalog.json`) est généré par
 * `pnpm --filter @campus/assets-cli ingest-pixel-citizen` depuis les fichiers
 * du style. Il ne contient que des métadonnées (identifiants, libellés,
 * couleurs des palettes) : les calques eux-mêmes restent sur le serveur.
 */

// ── Format ──────────────────────────────────────────────────────────────────

/** Choix d'un calque : identifiant + couleurs de ses sélections, dans l'ordre. */
export interface PcChoice {
  choice: string;
  colors: string[];
}

export type PcSlot = PcChoice | '%none%';

/**
 * Même structure que le champ `customization` des JSON exportés par TDSM
 * (vérification au pixel près contre les planches officielles).
 */
export interface PcCustomization {
  'skin-color': [string];
  /** Cuir chevelu, pilosité faciale, sourcils. */
  'hair-color': [string, string, string];
  'eye-color': [string];
  body: PcChoice;
  head: PcChoice;
  nose: PcSlot;
  eyes: PcChoice;
  'hair-front': PcSlot;
  'hair-back': PcSlot;
  'facial-hair': PcSlot;
  'face-accessory': PcSlot;
  headwear: PcSlot;
  top: PcSlot;
  outerwear: PcSlot;
  bottom: PcSlot;
  footwear: PcSlot;
}

/** Apparence stockée (`tenant_members.appearance`). `sheet` = empreinte de la planche assemblée. */
export interface PcAppearance {
  v: 2;
  pc: PcCustomization;
  sheet: string;
}

/**
 * Forme réduite diffusée aux autres joueurs par Colyseus (limite de 500
 * caractères côté serveur) : l'empreinte suffit pour charger la planche.
 * Vaut aussi pour les avatars « monde du campus » (v3) : même format de
 * planche, même route, le jeu ne fait aucune différence.
 */
export interface PcSheetRef {
  v: 2 | 3;
  sheet: string;
}

export function isPcSheetRef(value: unknown): value is PcSheetRef {
  const v = typeof value === 'object' && value !== null ? (value as { v?: unknown }).v : undefined;
  return (v === 2 || v === 3)
    && typeof (value as { sheet?: unknown }).sheet === 'string'
    && /^[0-9a-f]{32}$/.test((value as { sheet: string }).sheet);
}

/** JSON envoyé à Colyseus pour une apparence : forme réduite pour un avatar Pixel Citizen. */
export function wireAppearanceJson(appearance: object | null | undefined): string {
  if (!appearance) return '';
  if (isPcSheetRef(appearance)) return JSON.stringify({ v: appearance.v, sheet: appearance.sheet });
  return JSON.stringify(appearance);
}

// ── Sélections de couleur (fonctions `*_psf` du manifeste du style) ─────────

/** Une sélection : sa rampe, l'index de sa palette de référence `refs/clothing-{n}`. */
export interface PcSelectionSpec { ramp: string; refs: number; swatchColumn?: number; label: string }

const sel = (ramp: string, refs: number, label: string, swatchColumn?: number): PcSelectionSpec =>
  swatchColumn === undefined ? { ramp, refs, label } : { ramp, refs, label, swatchColumn };

/** Sélections de chaque type d'objet. Les objets sans type connu sont `basic`. */
export const PC_SELECTION_KINDS: Record<string, PcSelectionSpec[]> = {
  basic: [sel('basic-clothes', 0, 'Couleur')],
  glasses: [sel('glasses-frames', 0, 'Monture')],
  'sunglasses-sep-frame': [sel('sunglasses', 0, 'Verres'), sel('glasses-frames', 1, 'Monture')],
  'cowboy-hat': [sel('cowboy-hat', 0, 'Couleur'), sel('basic-clothes', 1, 'Ruban')],
  kabuto: [sel('samurai', 0, 'Armure A'), sel('metal', 1, 'Métal'), sel('samurai', 2, 'Armure B'), sel('basic-clothes', 3, 'Corde')],
  'flat-cap': [sel('tweed', 0, 'Couleur')],
  metal: [sel('metal', 0, 'Métal')],
  'lucha-libre-mask': [sel('basic-clothes', 0, 'Couleur'), sel('basic-clothes', 1, 'Accent'), sel('basic-clothes', 2, 'Symbole'), sel('basic-clothes', 3, 'Lacet')],
  'shirt-and-tie': [sel('basic-clothes', 0, 'Chemise'), sel('basic-clothes', 1, 'Cravate')],
  'trench-coat': [sel('coat', 0, 'Couleur'), sel('buttons', 1, 'Boutons', 2)],
  'denim-jacket': [sel('denim', 0, 'Jean'), sel('denim-stitching', 1, 'Coutures')],
  'varsity-jacket': [sel('basic-clothes', 0, 'Couleur'), sel('basic-clothes', 1, 'Manches')],
  cardigan: [sel('basic-clothes', 0, 'Couleur'), sel('buttons', 1, 'Boutons', 2)],
  formalwear: [sel('formalwear', 0, 'Couleur'), sel('buttons', 1, 'Boutons', 2)],
  denim: [sel('denim', 0, 'Jean')],
  trousers: [sel('formalwear', 0, 'Couleur'), sel('formal-footwear', 1, 'Ceinture')],
  'formal-metal-accent': [sel('formal-footwear', 0, 'Cuir'), sel('metal', 1, 'Boucle')],
  boots: [sel('basic-clothes', 0, 'Couleur'), sel('basic-clothes', 1, 'Semelle')],
  sneakers: [sel('basic-clothes', 0, 'Couleur'), sel('basic-clothes', 1, 'Lacets')],
};

/** Types reconnus par le manifeste pour chaque calque de vêtement. */
export const PC_LAYER_KINDS: Record<string, string[]> = {
  'face-accessory': ['glasses', 'sunglasses-sep-frame'],
  headwear: ['cowboy-hat', 'kabuto', 'flat-cap', 'metal', 'lucha-libre-mask'],
  top: ['shirt-and-tie'],
  outerwear: ['metal', 'trench-coat', 'denim-jacket', 'varsity-jacket', 'cardigan', 'formalwear'],
  bottom: ['denim', 'trousers'],
  footwear: ['formal-metal-accent', 'boots', 'sneakers'],
};

// ── Catalogue ───────────────────────────────────────────────────────────────

export interface PcCatalogChoice {
  id: string;
  label: string;
  /** Type de sélections (clé de PC_SELECTION_KINDS) — vêtements et accessoires uniquement. */
  kind?: string;
  /** Masquage des cheveux par un couvre-chef (`custom-mask`, `full-mask`, `chin-exposed`). */
  masking?: string;
  /** Hors palette : refusé pour une nouvelle apparence. */
  hidden?: boolean;
}

export interface PcCatalogLayer {
  label: string;
  /** « Aucun » autorisé. */
  optional: boolean;
  choices: PcCatalogChoice[];
}

export interface PcCatalog {
  styleId: string;
  frame: number;
  directions: string[];
  anims: Array<{ id: string; frames: number }>;
  /** Échantillons de chaque rampe de couleurs (vêtements). */
  palettes: Record<string, string[]>;
  /** Teintes de peau, couleurs de cheveux, couleurs d'yeux. */
  bio: { skin: string[]; hair: string[]; eyes: string[] };
  layers: Record<PcLayerId, PcCatalogLayer>;
}

export const PC_LAYERS = [
  'body', 'head', 'nose', 'eyes', 'hair-front', 'hair-back', 'facial-hair',
  'face-accessory', 'headwear', 'top', 'outerwear', 'bottom', 'footwear',
] as const;
export type PcLayerId = (typeof PC_LAYERS)[number];

export const pixelCitizenCatalog = rawCatalog as unknown as PcCatalog;

/** Sélections de couleur d'un choix (vide pour les calques sans couleur propre). */
export function pcChoiceSelections(layer: PcLayerId, choiceId: string): PcSelectionSpec[] {
  const choice = pixelCitizenCatalog.layers[layer].choices.find((c) => c.id === choiceId);
  if (!choice?.kind) return [];
  return PC_SELECTION_KINDS[choice.kind] ?? [];
}

/** Échantillons proposés pour une sélection. */
export function pcSwatches(spec: PcSelectionSpec): string[] {
  return pixelCitizenCatalog.palettes[spec.ramp] ?? [];
}

// ── Validation ──────────────────────────────────────────────────────────────

export type PcValidation =
  | { ok: true; value: PcCustomization }
  | { ok: false; error: string };

const HEX = /^#[0-9a-f]{6}$/;
const norm = (v: unknown) => (typeof v === 'string' ? v.toLowerCase() : '');

/**
 * Valide une apparence et la remet sous forme canonique (ordre des clés,
 * couleurs en minuscules) — la forme canonique sert d'empreinte de la planche.
 * Couleurs limitées aux échantillons des palettes : seules couleurs pour
 * lesquelles les ombres et contours sont garantis identiques à l'outil officiel.
 */
export function validatePcCustomization(input: unknown): PcValidation {
  if (typeof input !== 'object' || input === null) return { ok: false, error: 'NOT_AN_OBJECT' };
  const src = input as Record<string, unknown>;
  const cat = pixelCitizenCatalog;

  const bioColors = (key: string, count: number, palette: string[]): string[] | null => {
    const v = src[key];
    if (!Array.isArray(v) || v.length !== count) return null;
    const colors = v.map(norm);
    return colors.every((c) => HEX.test(c) && palette.includes(c)) ? colors : null;
  };
  const skin = bioColors('skin-color', 1, cat.bio.skin);
  if (!skin) return { ok: false, error: 'INVALID_SKIN_COLOR' };
  const hair = bioColors('hair-color', 3, cat.bio.hair);
  if (!hair) return { ok: false, error: 'INVALID_HAIR_COLOR' };
  const eyes = bioColors('eye-color', 1, cat.bio.eyes);
  if (!eyes) return { ok: false, error: 'INVALID_EYE_COLOR' };

  const out: Record<string, unknown> = {
    'skin-color': skin, 'hair-color': hair, 'eye-color': eyes,
  };
  for (const layer of PC_LAYERS) {
    const def = cat.layers[layer];
    const v = src[layer];
    if (v === '%none%' || v === undefined || v === null) {
      if (!def.optional) return { ok: false, error: `MISSING_${layer.toUpperCase()}` };
      out[layer] = '%none%';
      continue;
    }
    if (typeof v !== 'object') return { ok: false, error: `INVALID_${layer.toUpperCase()}` };
    const { choice, colors } = v as { choice?: unknown; colors?: unknown };
    const entry = def.choices.find((c) => c.id === choice);
    if (!entry || entry.hidden) return { ok: false, error: `UNKNOWN_${layer.toUpperCase()}` };
    const specs = pcChoiceSelections(layer, entry.id);
    const list = Array.isArray(colors) ? colors.map(norm) : [];
    if (list.length !== specs.length) return { ok: false, error: `INVALID_${layer.toUpperCase()}_COLORS` };
    for (const [i, spec] of specs.entries()) {
      if (!pcSwatches(spec).includes(list[i]!)) return { ok: false, error: `INVALID_${layer.toUpperCase()}_COLORS` };
    }
    out[layer] = { choice: entry.id, colors: list };
  }
  return { ok: true, value: out as unknown as PcCustomization };
}

/** Forme canonique sérialisée (entrée du calcul d'empreinte côté serveur). */
export function canonicalPcCustomization(cz: PcCustomization): string {
  const ordered: Record<string, unknown> = {
    'skin-color': cz['skin-color'], 'hair-color': cz['hair-color'], 'eye-color': cz['eye-color'],
  };
  for (const layer of PC_LAYERS) ordered[layer] = cz[layer];
  return JSON.stringify(ordered);
}
